// 큐브지옥 (Cube Hell) engine types.
// Rules: docs/wireframes/cube-hell/rule.md (2026-06-27 design-confirmed).
//
// 3×3×3 (27-cell) hidden-cube deduction. Each player hides 4 colors × 5 cubes
// (20 filled, 7 empty), each color a single 6-face-connected group. Players
// alternate STRUCTURED queries — CELL (x,y,z) → exact color/empty, or LAYER
// (axis,index,color) → that color's count in a 9-cell slice — against a truthful
// oracle, then GUESS the opponent's full 27-cell arrangement. First fully-correct
// guess wins; a wrong guess is penalized (guessPenalty). Hidden information: a
// player's own arrangement is private (opponent sees it masked); the
// query/response log is public.

export const CUBE_HELL_PLAYERS = ["p1", "p2"] as const;
export const CUBE_COLORS = ["red", "yellow", "green", "blue"] as const;
export const QUERY_AXES = ["x", "y", "z"] as const;

export type CubeHellPlayerId = (typeof CUBE_HELL_PLAYERS)[number];
export type CubeColor = (typeof CUBE_COLORS)[number];
export type QueryAxis = (typeof QUERY_AXES)[number];

/** A board cell: a cube color, or "empty". */
export type Cell = CubeColor | "empty";

export const GRID = 3;
export const CELL_COUNT = 27; // GRID ** 3
export const CUBES_PER_COLOR = 5;
export const FILLED_CELLS = 20; // CUBE_COLORS.length * CUBES_PER_COLOR
export const EMPTY_CELLS = 7; // CELL_COUNT - FILLED_CELLS

// Ruleset flags (parameterized — see rule.md §0).
export type GuessPenalty = "immediate-loss" | "turn-loss";
export const CUBE_HELL_DEFAULT_GUESS_PENALTY: GuessPenalty = "immediate-loss";
/** null = unlimited queries per player (efficiency is the soft pressure). */
export const CUBE_HELL_DEFAULT_MAX_QUERIES: number | null = null;

export type CubeHellPhase = "query" | "game-over";

export interface ValidationResult {
  readonly valid: boolean;
  readonly error?: string;
  readonly details?: Record<string, unknown>;
}

export interface CubeHellPlayer {
  readonly playerId: CubeHellPlayerId;
  readonly label: string;
}

/** A 27-cell arrangement indexed by cellIndex = x + y*3 + z*9. */
export type Arrangement = readonly Cell[];

// ---- Queries & actions ------------------------------------------------------

export interface QueryCellAction {
  readonly type: "QueryCell";
  readonly playerId: CubeHellPlayerId;
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export interface QueryLayerAction {
  readonly type: "QueryLayer";
  readonly playerId: CubeHellPlayerId;
  readonly axis: QueryAxis;
  readonly index: number;
  readonly color: CubeColor;
}

export interface GuessAction {
  readonly type: "Guess";
  readonly playerId: CubeHellPlayerId;
  /** The asker's full guess for the OPPONENT's 27-cell arrangement. */
  readonly arrangement: Arrangement;
}

export type CubeHellAction = QueryCellAction | QueryLayerAction | GuessAction;

// ---- Public query/guess log -------------------------------------------------

export interface CellQueryRecord {
  readonly kind: "cell";
  readonly seq: number;
  readonly askerId: CubeHellPlayerId;
  readonly targetId: CubeHellPlayerId;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly result: Cell;
}

export interface LayerQueryRecord {
  readonly kind: "layer";
  readonly seq: number;
  readonly askerId: CubeHellPlayerId;
  readonly targetId: CubeHellPlayerId;
  readonly axis: QueryAxis;
  readonly index: number;
  readonly color: CubeColor;
  readonly count: number;
}

export interface GuessRecord {
  readonly kind: "guess";
  readonly seq: number;
  readonly askerId: CubeHellPlayerId;
  readonly targetId: CubeHellPlayerId;
  readonly correct: boolean;
  /** Cells matched (0..27) — informational; there is no partial scoring. */
  readonly matched: number;
}

export type QueryRecord = CellQueryRecord | LayerQueryRecord | GuessRecord;

// ---- State ------------------------------------------------------------------

export interface CubeHellState {
  readonly gameId: string;
  readonly players: readonly CubeHellPlayer[];
  readonly phase: CubeHellPhase;
  readonly currentPlayerId: CubeHellPlayerId;
  /** Each player's hidden arrangement (server-only). */
  readonly arrangements: Readonly<Record<CubeHellPlayerId, Arrangement>>;
  /** Public log of every query/guess and its truthful response. */
  readonly log: readonly QueryRecord[];
  readonly queryCounts: Readonly<Record<CubeHellPlayerId, number>>;
  /** Seeded PRNG state for deterministic arrangement generation (server-only). */
  readonly rngState: number;
  readonly winnerId: CubeHellPlayerId | null;
  readonly lastAction: CubeHellAction | null;
  readonly moveNumber: number;
  // Ruleset (parameterized — see rule.md §0).
  readonly guessPenalty: GuessPenalty;
  readonly maxQueriesPerPlayer: number | null;
}

export interface InitGameOptions {
  readonly gameId?: string;
  readonly firstPlayerId?: CubeHellPlayerId;
  /** Seed for the deterministic arrangement generation. */
  readonly seed?: number;
  readonly guessPenalty?: GuessPenalty;
  readonly maxQueriesPerPlayer?: number | null;
}

// ---- Viewer projection ------------------------------------------------------
// A viewer sees their OWN arrangement; the opponent's is masked (null). The PRNG
// state is dropped entirely. The query/response log is public.
export interface CubeHellViewerState
  extends Omit<CubeHellState, "arrangements" | "rngState"> {
  readonly arrangements: Readonly<Record<CubeHellPlayerId, Arrangement | null>>;
}
